import React, { useCallback, useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import AppShell from 'app/AppShell';
import Header from 'shared/components/Header';
import Footer from 'shared/components/Footer';
import HaatBanner from 'features/onboarding/components/welcome/HaatBanner';
import HaatSteps from 'features/onboarding/components/welcome/HaatSteps';

import GrowFeatures from 'features/onboarding/components/welcome/GrowFeatures';
import WhySell from 'features/onboarding/components/welcome/WhySell';
import LogoMarquee from 'features/onboarding/components/welcome/LogoMarquee';
import GrowTogether from 'features/onboarding/components/welcome/GrowTogether';
import FaqSection from 'features/onboarding/components/welcome/FaqSection';


/**
 * Public landing page shown at `/`. Presents the value proposition, the
 * overview of the registration steps, and the list of documents the partner
 * should prepare. The "Start" CTAs navigate to the wizard at `/register`.
 */
const LandingPage: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const goToRegister = useCallback(() => navigate('/register'), [navigate]);
  const goToLogin = useCallback(() => navigate('/login'), [navigate]);

  // The header stays visible at all times; its "Become a partner" CTA is
  // revealed only once the user scrolls past the hero section.
  const [scrolledPastHero, setScrolledPastHero] = useState(false);
  const heroSentinelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const sentinel = heroSentinelRef.current;
    if (!sentinel) return undefined;

    const observer = new IntersectionObserver(
      ([entry]) => setScrolledPastHero(!entry.isIntersecting),
      { threshold: 0 },
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, []);

  return (
    <AppShell fullBleed hideHeader>
      <Header
        persistentActionLabel={t('header.login') as string}
        onPersistentActionClick={goToLogin}
        showCta={scrolledPastHero}
        ctaLabel={t('landingSections.banner.becomePartner') as string}
        onCtaClick={goToRegister}
      />

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, ease: 'easeOut' }}
      >
        <HaatBanner onStart={goToRegister} />
        <div ref={heroSentinelRef} aria-hidden="true" />
        <HaatSteps />
        <GrowFeatures />
        <WhySell />
        <LogoMarquee />
        {/* <TabletHero /> */}
        <GrowTogether onStart={goToRegister} />
        <FaqSection />
        {/* <DidYouKnowSection /> */}
        {/* <DocumentsBanner onStart={goToRegister} /> */}
      </motion.div>

      <Footer dark />
    </AppShell>
  );
};

export default LandingPage;
